import React, { useContext } from 'react'
import { getReadableTextColor } from '@helpers/color_scheme_helpers'
import { markupAndSanitize, sanitize } from '@utils/sanitize'
import FormattedTime from '@components/FormattedTime'
import InTimelineImageWithModal from './InTimelineImageWithModal'
import { ThemeContext } from '@base/ThemeContext'
import type { Photo } from '@app-types/entity_types'
import type { DerivedMarker, DerivedTimespan } from '../../types/derived_items'
import { isJsonapiPhoto } from '@app-types/entity_types'
import type { Bounds } from '../../types/types'

// Union type for both marker and timespan
type TimelineItem = DerivedMarker | DerivedTimespan

type ComponentProps = {
  item: TimelineItem
  photos?: Record<string, Photo>
  expandedItemBounds: Bounds
}

const DefaultTimelineItemDetail: React.FC<ComponentProps> = ({ item, photos = {}, expandedItemBounds }) => {
  const isMarker = (item: TimelineItem): item is DerivedMarker => 'date' in item
  const isTimespan = (item: TimelineItem): item is DerivedTimespan => 'start_date' in item

  // Get theme context for timespan styling
  const colorScheme = useContext(ThemeContext)
  const bodyBackgroundColor = colorScheme?.secondary
  const bodyTextColor = bodyBackgroundColor ? getReadableTextColor(bodyBackgroundColor, colorScheme) : undefined
  const headerBackgroundColor = bodyBackgroundColor
  const headerTextColor = bodyBackgroundColor ? getReadableTextColor(headerBackgroundColor, colorScheme) : undefined

  // Common properties
  const description = item.description || null
  const timelineItemStyle: React.CSSProperties = {
    width: expandedItemBounds.width,
    height: expandedItemBounds.height,
    ...(bodyBackgroundColor && { backgroundColor: bodyBackgroundColor }),
    ...(bodyTextColor && { color: bodyTextColor }),
  }

  const timelineItemContentStyle = {
    ...(headerBackgroundColor && { backgroundColor: headerBackgroundColor }),
    ...(headerTextColor && { color: headerTextColor }),
  }

  // Helper function to get image data
  const getImageData = (item: TimelineItem) => {
    if (!('photo_ids' in item)) return null

    const photoId = item.photo_ids.length > 0 ? item.photo_ids[0].toString() : null
    const photoData = photoId ? photos[photoId] : null

    if (!photoData) return null

    const photoAttributes = isJsonapiPhoto(photoData) ? photoData.attributes : photoData
    if (photoAttributes.image_processing_complete !== true) return null

    return {
      imageData: photoAttributes.image_urls?.medium_landscape,
      modalImageData: photoAttributes.image_urls?.medium,
    }
  }

  // Helper function to get date content
  const getDateContent = (item: TimelineItem) => {
    if (isMarker(item)) {
      return <FormattedTime dateTime={item.date} />
    }
    if (isTimespan(item)) {
      return (
        <>
          <FormattedTime dateTime={item.start_date} color={headerTextColor} /> &ndash;{' '}
          <FormattedTime dateTime={item.end_date} color={headerTextColor} />
        </>
      )
    }
    return null
  }

  // Render content based on item type
  const imageDataResult = getImageData(item)
  const imageData = imageDataResult?.imageData || null
  const modalImageData = imageDataResult?.modalImageData || null
  const dateContent = getDateContent(item)

  return (
    <div className="timeline-item-detail" style={timelineItemStyle}>
      {imageData && modalImageData && (
        <InTimelineImageWithModal
          image={imageData}
          modalImage={modalImageData}
          containerWidth={expandedItemBounds.width}
          containerHeight={expandedItemBounds.height * 0.5}
          enableModalViewing={true}
        />
      )}
      <div className="timeline-item-detail__content p-t p-b" style={timelineItemContentStyle}>
        <div className="timeline-item-detail__meta p-x">
          <div className="timeline-item-detail__time">{dateContent}</div>
          <h2 className="timeline-item-detail__title">{sanitize(item.title)}</h2>
        </div>
        {description && (
          <div
            className="timeline-item-detail__description block p-x marked-up"
            dangerouslySetInnerHTML={{ __html: markupAndSanitize(description) }}
          />
        )}
      </div>
    </div>
  )
}

export default DefaultTimelineItemDetail
